import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Users, Clock, Tag, Sparkles, AlertCircle, MapPin, Calendar } from 'lucide-react';
import { formatPrice, getCategoryInfo, formatTime, DAYS_OF_WEEK } from '../lib/utils';
import LazyClassImage from '../components/ui/LazyClassImage';

export const ClassCard = ({ classData, schedules = [], showAvailability = false }) => {
  const categoryInfo = getCategoryInfo(classData.category);
  const isMainCourse = classData.name === 'UPA 3 Hour Course';
  // Show bundle badge if there's a bundle price (for Street + Ballet/Tap combo)
  const hasBundle = classData.bundle_price && classData.bundle_price > 0;
  
  // Get the primary price to display (prefer 6-week price over term price)
  const getPrimaryPrice = () => {
    if (classData.six_week_price) return { price: classData.six_week_price, label: 'Per 6 weeks' };
    if (classData.term_price) return { price: classData.term_price, label: 'Per term' };
    if (classData.subscription_price) return { price: classData.subscription_price, label: 'Per month' };
    if (classData.one_off_price) return { price: classData.one_off_price, label: 'Per class' };
    return null;
  };
  
  const primaryPrice = getPrimaryPrice();
  
  // Check if class has its own schedule info (new fields)
  const hasClassSchedule = classData.day_of_week || classData.start_time;
  
  return (
    <Card 
      className={`group overflow-hidden border hover:shadow-neon-pink hover:-translate-y-1 transition-all duration-300 bg-[#111] ${
        isMainCourse ? 'border-[#FF007F]/50 ring-2 ring-[#FF007F]/20' : 'border-[#222]'
      }`}
      data-testid={`class-card-${classData.id}`}
    >
      <div className="relative h-48 overflow-hidden">
        <LazyClassImage 
          classId={classData.id}
          alt={classData.name}
          className="w-full h-full group-hover:scale-105 transition-transform duration-500"
          style={{ 
            objectPosition: classData.name === 'Junior Street Dance' ? 'center 20%' : 'center center' 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        {/* Top badges - category and class type */}
        <div className="absolute top-3 left-3 right-3 flex justify-between items-start">
          <div className="flex gap-1.5 flex-wrap max-w-[60%]">
            <Badge className="bg-[#7000FF] text-white text-xs">
              {categoryInfo.label}
            </Badge>
            {isMainCourse && (
              <Badge className="bg-gradient-to-r from-[#FF007F] to-[#7000FF] text-white border-0 text-xs">
                <Sparkles className="w-3 h-3 mr-1" />
                Core
              </Badge>
            )}
            {classData.is_addon && (
              <Badge className="bg-[#FFD700] text-black text-xs">
                Add-on
              </Badge>
            )}
          </div>
          {/* Limited Spots badge on right */}
          {showAvailability && (
            <Badge className="bg-orange-500 text-white font-semibold text-xs whitespace-nowrap">
              Limited Spots!
            </Badge>
          )}
        </div>
      </div>
      
      <CardContent className="p-5">
        <h3 className="font-heading text-xl font-semibold text-white mb-2 group-hover:text-[#FF007F] transition-colors">
          {classData.name}
        </h3>
        
        <p className="text-sm text-white/60 mb-4 line-clamp-2">
          {classData.description}
        </p>
        
        {/* Class schedule info from new fields */}
        {hasClassSchedule && (
          <div className="mb-3 space-y-1">
            <div className="flex items-center gap-2 text-sm text-cyan-400">
              <Clock className="w-4 h-4" />
              <span>
                {classData.day_of_week && <span>{classData.day_of_week}</span>}
                {classData.start_time && classData.end_time && (
                  <span> {classData.start_time} - {classData.end_time}</span>
                )}
                {classData.start_time && !classData.end_time && (
                  <span> {classData.start_time}</span>
                )}
              </span>
            </div>
            {classData.term_dates && (
              <div className="flex items-center gap-2 text-sm text-purple-400">
                <Calendar className="w-4 h-4" />
                <span>{classData.term_dates}</span>
              </div>
            )}
            {classData.address && (
              <div className="flex items-center gap-2 text-sm text-white/50">
                <MapPin className="w-4 h-4" />
                <span>{classData.address}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Duration */}
        {!hasClassSchedule && (
          <div className="flex flex-wrap gap-3 mb-4 text-sm text-white/50">
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{classData.duration_minutes >= 60 
                ? `${Math.floor(classData.duration_minutes / 60)}${classData.duration_minutes % 60 > 0 ? `.${classData.duration_minutes % 60}` : ''} hr${classData.duration_minutes >= 120 ? 's' : ''}`
                : `${classData.duration_minutes} min`
              }</span>
            </div>
          </div>
        )}
        
        {/* Legacy schedule display (from schedules system) */}
        {!hasClassSchedule && schedules.length > 0 && (
          <div className="mb-4 text-sm">
            <p className="text-white/50 mb-1">Schedule:</p>
            <div className="flex flex-wrap gap-1">
              {schedules.slice(0, 2).map((schedule, idx) => (
                <Badge 
                  key={idx} 
                  variant="secondary" 
                  className="text-xs bg-[#222] text-white/70"
                >
                  {DAYS_OF_WEEK[schedule.day_of_week]} {formatTime(schedule.start_time)}
                </Badge>
              ))}
              {schedules.length > 2 && (
                <Badge variant="secondary" className="text-xs bg-[#222] text-white/70">
                  +{schedules.length - 2} more
                </Badge>
              )}
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between pt-4 border-t border-[#222]">
          <div>
            {primaryPrice ? (
              <>
                <p className="text-xs text-white/50">{primaryPrice.label}</p>
                <div className="flex items-center gap-2">
                  <p className="font-mono font-semibold text-lg text-[#FF007F]">
                    £{primaryPrice.price.toFixed(2)}
                  </p>
                  {hasBundle && (
                    <Badge className="bg-[#FFD700]/20 text-[#FFD700] text-xs">
                      <Tag className="w-3 h-3 mr-1" />
                      £{classData.bundle_price.toFixed(2)} for both
                    </Badge>
                  )}
                </div>
              </>
            ) : (
              <p className="text-sm text-white/40 italic">Contact for pricing</p>
            )}
          </div>
          <Link to={`/classes/${classData.id}`}>
            <Button 
              size="sm" 
              className="bg-[#FF007F] hover:bg-[#D6006A] text-white rounded-none"
              data-testid={`view-class-${classData.id}`}
            >
              View Details
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

export default ClassCard;
